<template>
  <div style="background-color: white; padding: 20px 20px; text-align: justify">
    <span style="font-size: 20px; font-weight: bolder">
      To enhance the investigation of perturbed gene expression in tumors, we
      developed a "Cancer" module. This module identifies perturbed genes
      exhibiting differential expression across various tumors and consolidates
      a variety of information, including survival analysis, copy number
      variations, and more. This integration offers users comprehensive
      insights. The specifics are outlined below.</span
    >
    <br />
    <br />
    <span style="font-size: 18px; text-align: left"
      >By selecting different tumor types, perturbed genes with distinct
      expression patterns between tumor and normal samples in various cancer
      types are loaded. This table is sorted in descending order based on
      Log2FC.</span
    >
    <div style="text-align: center">
      <img
        width="60%"
        height="60%"
        src="http://research.gzsys.org.cn/perturbdb/image/helpDocument/1Cancers1.png"
      />
    </div>
    <br />
    <span style="text-align: left; color: black; font-size: 16px"
      >1. PerturbDB generates box plots for comparing expression in cancers. The
      expression data are first log2(TPM+1) transformed for differential
      analysis and the log2FC is defined as median(Tumor) - median(Normal).
      Clicking on the red data label in the second column opens a pop-up window
      showcasing box plot results for the differential analysis. Different file
      formats can be exported from this outcome.
    </span>
    <div style="text-align: center; margin-top: 10px">
      <img
        width="60%"
        height="60%"
        src="http://research.gzsys.org.cn/perturbdb/image/helpDocument/1Cancers2.png"
      />
    </div>
    <br />
    <span
      style="text-align: left; color: black; font-size: 16px; margin-left: 40px"
    >
      The content in the third column represents the p-value from the
      significance test for the differential expression analysis. The
      differential analysis is performed using one-way ANOVA, with the disease
      state (Tumor or Normal) used as a variable.
    </span>
    <br />
    <br />
    <span style="text-align: left; color: black; font-size: 16px"
      >2. The fourth column 'OS p-value' indicates the overall survival
      significance for patients with perturbed genes in this row within the
      tumor. Values equal to or less than 0.05 are considered significant.
      Clicking the purple tag opens a new window for analyzing patient survival.
      <i style="color: red"
        >By default, we group patients based on the median gene expression
        levels.</i
      >
    </span>
    <div style="text-align: center; margin-top: 10px">
      <img
        width="60%"
        height="60%"
        src="http://research.gzsys.org.cn/perturbdb/image/helpDocument/1Cancers3.png"
      />
    </div>
    <br />
    <span
      style="text-align: left; color: black; font-size: 16px; margin-left: 40px"
      >In this pop-up, the graphs will be automatically updated and loaded as
      users switch between tumor types, patient grouping methods or adjusting
      colors.
    </span>
    <div style="text-align: center; margin-top: 10px">
      <img
        width="60%"
        height="60%"
        src="http://research.gzsys.org.cn/perturbdb/image/helpDocument/1Cancers4.png"
      />
    </div>
    <br />
    <br />
    <span style="text-align: left; color: black; font-size: 16px"
      >3. In the fifth column, we present the correlation between copy number
      variation and mRNA expression levels of perturbed genes. The calculation
      employs Pearson correlation coefficient. The number indicates the specific
      correlation (R-value) for the selected tumor type. Clicking on the number
      opens a more detailed page.
    </span>
    <div style="text-align: center; margin-top: 10px">
      <img
        width="60%"
        height="60%"
        src="http://research.gzsys.org.cn/perturbdb/image/helpDocument/1Cancers5.png"
      />
    </div>
    <br />
    <span
      style="text-align: left; color: black; font-size: 16px; margin-left: 40px"
      >The popped-up window displays the correlation between copy number
      variation and mRNA expression levels across tumors. It is sorted in
      descending order based on the R-values in the second column. Clicking on
      the last column generates a scatter plot illustrating the correlation and
      offers a download option.
    </span>
    <div style="text-align: center; margin-top: 10px">
      <img
        width="60%"
        height="60%"
        src="http://research.gzsys.org.cn/perturbdb/image/helpDocument/1Cancers6.png"
      />
      <img
        width="60%"
        height="60%"
        src="http://research.gzsys.org.cn/perturbdb/image/helpDocument/1Cancers7.png"
      />
    </div>
    <br />
    <br />
    <span style="text-align: left; color: black; font-size: 16px"
      >4. In the sixth column, we provide the identification results of
      cis-eQTLs. Expression quantitative trait loci (eQTLs) are regions of the
      genome containing DNA sequence variants that influence the expression
      level of genes. The numbers in the table represent the count of identified
      cis-eQTLs in cancer.
    </span>
    <div style="text-align: center">
      <img
        width="60%"
        height="60%"
        src="http://research.gzsys.org.cn/perturbdb/image/helpDocument/1Cancers8.png"
      />
    </div>
    <br />
    <span
      style="text-align: left; color: black; font-size: 16px; margin-left: 40px"
    >
      The popup presents comprehensive details about the cis-eQTLs associated
      with the perturbed gene, encompassing information such as location, SNP,
      significance p-value, and additional details.
    </span>
    <div style="text-align: center; margin-top: 10px">
      <img
        width="60%"
        height="60%"
        src="http://research.gzsys.org.cn/perturbdb/image/helpDocument/1Cancers9.png"
      />
    </div>
    <br />
    <br />
    <span style="text-align: left; color: black; font-size: 16px"
      >5. In the seventh column, we present a compilation of genes exhibiting
      similar expression patterns. Users can click on the icon to access a
      detailed page for further exploration.
    </span>
    <div style="text-align: center; margin-top: 10px">
      <img
        width="60%"
        height="60%"
        src="http://research.gzsys.org.cn/perturbdb/image/helpDocument/1Cancers10.png"
      />
    </div>
    <br />
    <span
      style="text-align: left; color: black; font-size: 16px; margin-left: 40px"
    >
      In the popup, users can select the tumor type of interest and the number
      of top similar genes to be displayed. The list provides detailed
      information about these similar genes. Clicking on the last column
      generates a scatter plot depicting similarity and offers a download
      option.
    </span>
    <div style="text-align: center; margin-top: 10px">
      <img
        width="60%"
        height="60%"
        src="http://research.gzsys.org.cn/perturbdb/image/helpDocument/1Cancers11.png"
      />
    </div>
    <br />
    <br />
    <span style="text-align: left; color: black; font-size: 16px"
      >5. In the eighth column, we have compiled the results of Hallmark
      enrichment analysis, comparing perturbed cells to control cells for the
      perturbed gene. Clicking the 'Explore' button will open a detailed page.
      Users can click the tabs to navigate between enrichment results for
      different datasets.
    </span>
    <div style="text-align: center; margin-top: 10px">
      <img
        width="60%"
        height="60%"
        src="http://research.gzsys.org.cn/perturbdb/image/helpDocument/1Cancers12.png"
      />
    </div>
    <br />
  </div>
</template>

<script>
export default {};
</script>

<style></style>
